import type {
  BuilderPricingVisibilitySettings,
  BuilderLocationSettings,
  BuilderProducts,
} from "api-types/builders/builderTypes";
import type { PlanImages } from "api-types/homePlans/homePlanTypes";
import type { UserAdminRole } from "api-types/users/userAdminRole";
import type { StudioPrefs } from "api-types/users/usersRequests";
import type { GarageHandingDirection } from "building-types/building/garageHanding";
import type { BuildingStats } from "building-types/options/buildingStats";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionSetOverrides, PlanStats } from "building-types/options/optionTemplateTypes";
import type { FeatureName } from "common/constants/features";
import type { BuilderSalesConfig } from "./builders/salesConfig";

export type FeatureFlags = {
  [key in FeatureName]?: boolean;
};

export interface BuilderUser {
  readonly builderId: string | null;
  readonly userId: string /* cast-id-to-number */;
  readonly externalId: string | null /* no-query: null */;
  readonly title: string;
  readonly featureFlags: FeatureFlags;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export interface User {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly phone: string;
  /**
   * @deprecated Prefer userCanAccess for determining a user's admin access level.
   */
  readonly adminRole: UserAdminRole;
  readonly tempUserRole: string | null;
  readonly featureFlags: FeatureFlags;
  readonly studioPrefs: StudioPrefs;
  readonly confirmedAt: Date;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

// Value used for elevations for which image generation fails
export const MISSING_ELEVATION_IMAGE_SENTINEL = "z";

/**
 * no-import
 */
export type Pricing = {
  readonly optionsCount: number;
  readonly optionsPrice: number;
  readonly totalPrice: number;
  readonly basePrice: number;
  readonly lotPrice: number;
  readonly discount?: number;
};

export interface MasterPlan {
  readonly id: string;
  readonly externalId: string | null /* no-query: null */;
  readonly templateCode: string;
  readonly studioDocId: string | null;
  readonly builderId: string | null;
  readonly name: string;
  readonly thumbnailUrl: string;
  readonly description: string;
  readonly basePrice: number;
  readonly stats: PlanStats;
  readonly planImages: PlanImages;
  readonly planDownloadEnabled: boolean;
  readonly disableShowroomViewer: boolean;
  readonly overrides: OptionSetOverrides | null /* no-query: null */;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly detached: boolean;
  readonly detachedFromId: string | null /* table: master_plans */;
}

/**
 * no-import
 */
export enum BuilderDocumentType {
  DOCUMENTATION = "Documentation",
  SALES_BROCHURE = "Sales Brochure",
  PLAN_SET = "Plan Set",
  KBM = "Key Building Measures",
  DXF = "AutoCAD DXF",
  GLTF = "glTF",
}

/**
 * no-import
 */
export type HomePlanCustomerField = "externalId" | "name" | "email" | "phone";
export type HomePlanCustomerInfo = Partial<Record<HomePlanCustomerField, string>>;

/**
 * no-import
 */
export type BuilderBrochureImageOption = "3d-line" | "3d-view" | "image";

export type BuilderCategory = "CUSTOMER" | "DEMO" | "PROSPECT" | "TESTING" | "TRAINING" | "LEGACY";

/**
 * no-import
 */
export type BuilderBrochurePlanLabelFormat = "feet-inches" | "whole-foot";

export type BuilderBrochureSettings = {
  readonly imageType: BuilderBrochureImageOption;
  readonly displayOptionPrices: boolean | undefined;
  readonly displayOptionDescriptions: boolean;
  readonly displayAllOptions: boolean;
  readonly displayFairHousingLogo: boolean;
  readonly displayElevationPage: boolean;
  readonly displayDefaultExteriorPaletteWhenSelected: boolean;
  readonly displaySqft: boolean;
  readonly displayPlanLabelDims: boolean;
  readonly displayElectricalOptions: boolean;
  readonly groupOptionsBySalesCategory: boolean;
  readonly legalDisclaimer: string;
  readonly website: string;
  readonly logoUrl: string;
  readonly singlePlanPerPage: boolean;
  readonly addElectricalPlans: boolean;
};
export interface Builder {
  readonly id: string;
  readonly category: BuilderCategory;
  readonly companyName: string;
  readonly website: string;
  readonly phone: string;
  readonly address: string;
  readonly city: string;
  readonly state: string;
  readonly zip: string;
  readonly country: string;
  readonly logoUrl: string;
  readonly featureFlags: FeatureFlags;
  readonly enabledProducts: BuilderProducts;
  readonly brochureSettings: BuilderBrochureSettings;
  readonly salesConfig: BuilderSalesConfig;
  readonly pricingVisibilitySettings: BuilderPricingVisibilitySettings;
  readonly locationSettings: BuilderLocationSettings;
  readonly creditReloadThreshold: number;
  readonly creditReloadAmount: number;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export type HomePlanStatus = "PREP" | "PRE_CONSTRUCTION" | "CONSTRUCTION";

export type HomePlanCategory = "prospect" | "contract" | "testing" | "closed";

export type HomePlanSource = "web_lead" | "sales_agent";

export type HomePlanStats = BuildingStats &
  Pricing & {
    readonly elevation: string;
    readonly optionCount?: number; // @deprecated replaced by Pricing.optionsCount
    readonly price?: number; // @deprecated replaced by Pricing.totalPrice
    readonly paletteName?: string;
  };

export type HomePlanLockedAssets = {
  readonly planSetUrl: string;
};

/**
 * no-import
 */
export type PlanType = "studio_doc" | "master_plan" | "community_plan" | "lot" | "home_plan";

export interface HomePlan {
  readonly id: string;
  readonly externalId: string | null /* no-query: null */;
  readonly builderId: string;
  readonly authorId: string | null /* table: users */;
  readonly salesStaffId: string | null /* table: users */;
  readonly communityPlanId: string | null;
  readonly planName: string;
  readonly planStatus: HomePlanStatus;
  readonly category: HomePlanCategory;
  readonly source: HomePlanSource | null;
  readonly customerInfo: HomePlanCustomerInfo;
  readonly lotId: string | null;
  readonly notes: string;
  readonly optionChoices: OptionChoices;
  readonly stats: HomePlanStats;
  readonly thumbnailUrl: string;
  readonly lockedAssets: HomePlanLockedAssets;
  readonly archivedAt: Date;
  readonly lockedAt: Date | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly detachedAt: Date;
  readonly masterPlanOptionTemplate: OptionSetOverrides;
  readonly communityPlanOptionTemplate: OptionSetOverrides;
  readonly studioDocVersionId: string | null /* table: studio_doc_versions */;
  readonly totalPriceOverride: number | null;
}

/**
 * no-import
 */
export type HomePlanFilters = Pick<HomePlan, "planStatus" | "category" | "source" | "lockedAt" | "detachedAt"> &
  Pick<Lot, "lotStatus" | "spec">;

export interface CommunityPlan {
  readonly id: string;
  readonly externalId: string | null /* no-query: null */;
  readonly communityId: string | null;
  readonly masterPlanId: string | null;
  readonly userId: string | null;
  readonly name: string;
  readonly basePrice: number;
  readonly thumbnailUrl: string;
  readonly planImages: PlanImages;
  readonly stats: PlanStats;
  readonly planDownloadEnabled: boolean;
  readonly disableNewOptions: boolean;
  readonly archivedAt: Date;
  readonly overrides: OptionSetOverrides | null /* no-query: null */;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly detached: boolean;
  readonly detachedFromId: string | null /* table: community_plans */;
}

export interface Community {
  readonly id: string;
  readonly templateCode: string;
  readonly builderId: string | null;
  readonly salesStaffId: string | null /* table: users */;
  readonly name: string;
  readonly address: string;
  readonly city: string;
  readonly state: string;
  readonly zip: string;
  readonly country: string;
  readonly displayOptionPrices: boolean;
  readonly disableShowroomViewer: boolean;
  readonly salesMapUrl: string;
  readonly salesMapUseShowroomIntro: boolean;
  readonly brochureSlipSheetUrl: string;
  readonly generalNotes: string;
  readonly govtCodeAndStandards: string;
  readonly buildingCodeAnalysisDesignCriteria: string;
  readonly brochureSettings: BuilderBrochureSettings;
  readonly archivedAt: Date;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export type LotStatus = "available" | "unavailable" | "pending" | "sold" | "closed";

export type LotSpec = "none" | "spec" | "model";

export interface Lot {
  readonly id: string;
  readonly communityId: string | null;
  readonly externalId: string | null /* no-query: null */;
  readonly spec: LotSpec;
  readonly lotNumber: string;
  readonly address: string;
  readonly price: number;
  readonly phase: string;
  readonly block: string;
  readonly lotStatus: LotStatus;
  readonly lotTypes: string[];
  readonly lotFeatures: string[];
  readonly series: string;
  readonly width: number;
  readonly depth: number;
  readonly area: number;
  readonly releasedAt: Date;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly garageHandingDirection: GarageHandingDirection;
  readonly externalUrl: string;
}

export interface LotCommunityPlan {
  readonly id: string;
  readonly lotId: string | null;
  readonly communityPlanId: string | null;
  readonly overrides: OptionSetOverrides | null /* no-query: null */;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export enum StudioDocUserRole {
  OWNER = "owner",
  EDITOR = "editor",
  VIEWER = "viewer",
}

export interface StudioDocUser {
  readonly id: string;
  readonly studioDocId: string /* cast-id-to-number */;
  readonly userId: string /* cast-id-to-number */;
  readonly userRole: StudioDocUserRole;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly builderId: string | null;
}

export interface StudioDocBuilder {
  readonly id: string;
  readonly studioDocId: string | null;
  readonly builderId: string | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export interface StudioDocVersion {
  readonly id: string;
  readonly studioDocId: string /* cast-id-to-number */;
  readonly parent: string;
  readonly description: string;
  readonly publishedAt: Date;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly url: string;
  readonly isRefable: boolean;
}

export interface UserInvite {
  readonly id: string;
  readonly userId: string | null;
  readonly inviterId: string | null /* table: users */;
  readonly expiresAt: Date;
  readonly confirmedAt: Date;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly builderId: string | null;
  readonly note: string;
}
